import { useState, useEffect, useCallback } from 'react'
import { useDynamicPricing } from './dynamic-pricing.fetch'

type UseDualCurrencyPricingOptions = {
  /** Debounce delay in milliseconds (default: 500ms) */
  debounceMs?: number
  /** Initial annual spend value */
  initialSpend?: number
  /** Billing cycle (default: ANNUAL) */
  billingCycle?: 'MONTH' | 'ANNUAL'
  /** Amount limit */
  amountLimit?: number
  /** External annual spend value (for synchronization across multiple hooks) */
  externalAnnualSpend?: string
}

/**
 * Hook to fetch dynamic pricing for both USD and INR simultaneously
 * This ensures both currencies are fetched with the same spend amount
 *
 * @example
 * ```tsx
 * const {
 *   pricingUSD,
 *   pricingINR,
 *   annualSpend,
 *   setAnnualSpend,
 *   isLoading
 * } = useDualCurrencyPricing({
 *   initialSpend: 20000,
 *   debounceMs: 500,
 * })
 * ```
 */
export const useDualCurrencyPricing = ({
  debounceMs = 500,
  initialSpend = 200000,
  billingCycle = 'ANNUAL',
  amountLimit,
  externalAnnualSpend,
}: UseDualCurrencyPricingOptions = {}) => {
  // Shared annual spend state (use external if provided)
  const [internalAnnualSpend, setInternalAnnualSpend] = useState<string>(
    initialSpend.toString()
  )

  const annualSpend = externalAnnualSpend ?? internalAnnualSpend

  // Track if we need to update the hooks
  const [shouldUpdate, setShouldUpdate] = useState(false)

  // USD pricing hook
  const usdHook = useDynamicPricing({
    initialSpend,
    debounceMs,
    currency: 'USD',
    billingCycle,
    amountLimit,
  })

  // INR pricing hook
  const inrHook = useDynamicPricing({
    initialSpend,
    debounceMs,
    currency: 'INR',
    billingCycle,
    amountLimit,
  })

  // Sync annual spend to both hooks when it changes
  useEffect(() => {
    if (shouldUpdate || externalAnnualSpend) {
      usdHook.setAnnualSpend(annualSpend)
      inrHook.setAnnualSpend(annualSpend)
      setShouldUpdate(false)
    }
  }, [annualSpend, shouldUpdate, externalAnnualSpend, usdHook, inrHook])

  // Custom setAnnualSpend that updates both hooks
  const handleSetAnnualSpend = useCallback((value: string) => {
    setInternalAnnualSpend(value)
    setShouldUpdate(true)
  }, [])

  return {
    /** USD pricing data */
    pricingUSD: usdHook.pricing,
    /** INR pricing data */
    pricingINR: inrHook.pricing,
    /** Whether either hook is loading */
    isLoading: usdHook.isLoading || inrHook.isLoading,
    /** Any error from either hook */
    error: usdHook.error || inrHook.error,
    /** Current annual spend value */
    annualSpend,
    /** Function to update the annual spend (triggers debounced fetch for both) */
    setAnnualSpend: handleSetAnnualSpend,
    /** Force refetch for both currencies */
    refetch: () => {
      void usdHook.refetch()
      void inrHook.refetch()
    },
  }
}
